import React from 'react';
import { FileText, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';

const Terms = () => {
  const sections = [
    {
      title: '1. Service Agreement',
      content: `By using Rocket Van Express's delivery and mini-move services, you agree to these Terms and Conditions. These terms constitute a binding agreement between you ("Customer") and Rocket Van Express ("Service Provider", "we", "us").`
    },
    {
      title: '2. Services Offered',
      content: `Rocket Van Express provides:
• Local delivery services for small to medium items
• Mini-move and small removal services
• Business account services with monthly billing
• Services are available in Stellenbosch, Cape Town, and surrounding Western Cape areas as specified on our Areas page.`
    },
    {
      title: '3. Booking and Quotes',
      content: `• All quotes are estimates based on information provided
• Final pricing may vary based on actual load size, distance, access conditions, and time required
• Quotes are valid for 7 days from issue date
• Booking confirmation is provided via WhatsApp
• We reserve the right to decline bookings that fall outside our service capabilities`
    },
    {
      title: '4. Service Tiers',
      content: `Rocket Van NOW: Urgent on-demand service, subject to availability
Rocket Van TODAY: Same-day scheduled service with pickup window
Rocket Van TOMORROW: Next-day scheduled service
Availability and timing depend on current demand and service area. We will communicate any delays or changes immediately.`
    },
    {
      title: '5. Payment Terms',
      content: `Individual Customers:
• Payment required before or immediately after service completion
• Accepted methods: EFT, card payment, SnapScan
• Invoices provided via WhatsApp/email

Business Accounts:
• Payment terms: 7, 14, or 30 days as per account agreement
• Monthly consolidated invoicing
• Late payments may incur interest charges`
    },
    {
      title: '6. Cancellation and Rescheduling',
      content: `• Cancellations more than 2 hours before scheduled pickup: No charge
• Cancellations within 2 hours of pickup: 50% of quoted fee
• Failed pickup due to customer unavailability: Full quoted fee
• Rescheduling: Free if done at least 4 hours before pickup
• Contact us immediately via WhatsApp for any changes`
    },
    {
      title: '7. Customer Responsibilities',
      content: `You are responsible for:
• Providing accurate pickup and delivery addresses
• Ensuring items are ready for collection at scheduled time
• Providing accurate description and weight of items
• Securing valuable or fragile items appropriately
• Providing access codes, parking arrangements, or special instructions
• Being available for collection and delivery, or appointing a representative
• Declaring any prohibited or restricted items`
    },
    {
      title: '8. Prohibited Items',
      content: `We do NOT transport:
• Illegal substances or contraband
• Hazardous materials (explosives, flammables, toxic substances)
• Perishable food requiring refrigeration (unless pre-arranged)
• Live animals
• Weapons or ammunition
• Cash or negotiable instruments exceeding R5,000
• Items requiring specialized transport vehicles`
    },
    {
      title: '9. Liability and Insurance',
      content: `• We exercise care in handling all items with straps, blankets, and proper loading
• Our liability is limited to R10,000 per booking for loss or damage
• Customers should declare high-value items (>R10,000) for additional insurance (available at extra cost)
• We are not liable for damage to items improperly packed by customers
• Fragile items must be declared and properly packed
• Proof-of-delivery photos serve as evidence of condition upon delivery
• Claims must be reported within 24 hours of delivery with photographic evidence`
    },
    {
      title: '10. Delays and Force Majeure',
      content: `We strive for on-time service but are not liable for delays caused by:
• Traffic congestion or road closures
• Weather conditions
• Vehicle breakdowns (we will arrange alternative transport)
• Acts of God, civil unrest, or government restrictions
• Customer-related delays (unavailable, incorrect address, access issues)
Customers will be notified immediately of any significant delays.`
    },
    {
      title: '11. Dispute Resolution',
      content: `• All disputes should first be reported via WhatsApp or email
• We aim to resolve complaints within 48 hours
• If unresolved, disputes may be escalated to mediation
• These terms are governed by South African law
• Jurisdiction: Western Cape courts`
    },
    {
      title: '12. Business Account Terms',
      content: `Additional terms for business accounts:
• Monthly consolidated invoicing
• Payment terms as per signed agreement
• Priority dispatch subject to availability
• Dedicated account contact provided
• Service-level expectations as per package tier
• Account may be suspended for non-payment
• 30-day notice required for account cancellation`
    },
    {
      title: '13. Privacy and Data',
      content: `• We collect personal information to provide services
• Data is used for booking, communication, and invoicing
• We comply with POPIA (Protection of Personal Information Act)
• See our Privacy Policy for full details
• Customer data is not shared with third parties except for service delivery`
    },
    {
      title: '14. Communication',
      content: `• Primary communication is via WhatsApp
• You consent to receive service updates, booking confirmations, and promotional messages
• Opt out of marketing messages anytime via WhatsApp
• Critical service updates cannot be opted out of`
    },
    {
      title: '15. Modifications to Terms',
      content: `• We reserve the right to modify these terms at any time
• Changes will be communicated via website update
• Continued use of services constitutes acceptance of modified terms
• Material changes will be communicated directly to active customers`
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero */}
      <section className="bg-gradient-to-br from-[#001A47] via-[#002B6B] to-[#001A47] text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <a href="/" className="inline-flex items-center text-gray-300 hover:text-white mb-6 transition-colors">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Home
            </a>
            <FileText className="h-12 w-12 text-[#039fff] mb-4" />
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Terms & Conditions</h1>
            <p className="text-gray-300">Last updated: January 30, 2025</p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-sm p-8 md:p-12">
            <div className="prose prose-lg max-w-none">
              <p className="text-gray-600 mb-8">
                Please read these Terms and Conditions carefully before using Rocket Van Express's services. 
                By booking or using our services, you acknowledge that you have read, understood, 
                and agree to be bound by these terms.
              </p>

              <div className="space-y-8">
                {sections.map((section, index) => (
                  <div key={index} id={`section-${index + 1}`} className="scroll-mt-20">
                    <h2 className="text-2xl font-bold text-[#001A47] mb-4">{section.title}</h2>
                    <div className="text-gray-700 whitespace-pre-line leading-relaxed">
                      {section.content}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-12 p-6 bg-[#039fff]/5 rounded-lg border-l-4 border-[#039fff]">
                <h3 className="font-bold text-[#001A47] mb-2">Questions about these terms?</h3>
                <p className="text-gray-700 mb-4">
                  Contact us at {' '}
                  <a href="mailto:Info@rocketvan.co.za" className="text-[#039fff] hover:underline">
                    Info@rocketvan.co.za
                  </a>
                  {' '} or via WhatsApp at +27 736596239
                </p>
              </div>

              <div className="mt-8 flex gap-4">
                <a href="/privacy">
                  <Button variant="outline">Privacy Policy</Button>
                </a>
                <a href="/contact">
                  <Button variant="outline">Contact Us</Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Terms;
